import { useCallback, useState } from 'react';
import type { UseLRU, UseLRUProps, LRUState } from './types';

import { useDeclarationContext } from '10.quickConnect.app/components/domain/Declaration/context/declarationContext';
import { useStore } from '30.quickConnect.Stores';

import useEffectOnce from '80.quickConnect.Core/hooks/useEffectOnce';

export default function useLRU({ fullId, lruCount }: UseLRUProps): UseLRU {
  // State
  const [lruState, setLruState] = useState<LRUState>({
    list: [],
    showList: false,
  });

  // Store
  const {
    DeclarationStore: { deleteLRUValue, getLRUDescriptionsByFieldId },
    LoginStore: {
      signInInfos: { userUPN },
    },
  } = useStore();

  // Contexts
  const declaration = useDeclarationContext();

  const displayList = useCallback(() => {
    setLruState((previousState: LRUState) => ({
      ...previousState,
      showList: true,
    }));
  }, []);

  const hideList = useCallback(() => {
    setLruState((previousState: LRUState) => ({
      ...previousState,
      showList: false,
    }));
  }, []);

  const deleteLRU = useCallback(
    async (value: string): Promise<void> => {
      await deleteLRUValue(declaration, fullId, userUPN, value);

      setLruState((previousState: LRUState) => {
        const nextList = previousState.list.filter((item: string) => item !== value);

        return {
          ...previousState,
          list: nextList,
          showList: nextList.length > 0 ? previousState.showList : false,
        };
      });
    },
    [fullId, deleteLRUValue, declaration, userUPN],
  );

  useEffectOnce(() => {
    const fetchLRU = async () => {
      const lruList = await getLRUDescriptionsByFieldId(declaration, fullId, lruCount ?? 3);
      setLruState((previousState: LRUState) => ({
        ...previousState,
        list: lruList,
      }));
    };

    fetchLRU();
  });

  return {
    lruState,
    displayList,
    hideList,
    deleteLRU,
  };
}
